// src/app/services/home-design.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, Subject } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';

export interface HomeDesign {
  imageList: string[];
}

@Injectable({
  providedIn: 'root'
})
export class HomeDesignService {

  // Base URL for the /home endpoint (without trailing slash)
  private base_path = 'https://app.titv-store-api.com/home';

  // Subject to notify other components of changes (if needed)
  private refreshData = new Subject<void>();

  constructor(private http: HttpClient) { }

  get refreshNeeded() {
    return this.refreshData;
  }

  // ---------- Token & Headers ----------
  private getToken(): string {
    try {
      const authUser = sessionStorage.getItem('auth-user');
      if (authUser) {
        const userObj = JSON.parse(authUser);
        return userObj.token || '';
      }
      return '';
    } catch {
      return '';
    }
  }

  private getHeaders(includeAuth: boolean = true): HttpHeaders {
    let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers = headers.set('Authorization', 'Bearer ' + token);
      }
    }
    return headers;
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Une erreur est survenue';
    if (error.status === 403) {
      errorMessage = `Accès interdit. Token: ${this.getToken() ? 'présent' : 'absent'}`;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else if (error.status === 404) {
      errorMessage = 'HomeDesign non trouvé.';
    } else {
      errorMessage = `Erreur ${error.status}: ${error.message}`;
    }
    console.error('Erreur HomeDesignService:', errorMessage);
    return throwError(() => new Error(errorMessage));
  }

  // ---------- Public GET (no token required) ----------
  getHomeDesign(): Observable<HomeDesign> {
    const headers = this.getHeaders(false); // No auth needed for GET
    return this.http.get<HomeDesign>(this.base_path, { headers })
      .pipe(catchError(this.handleError));
  }

  // ---------- Admin PUT (requires token) ----------
  updateHomeDesign(imageList: string[]): Observable<HomeDesign> {
    const headers = this.getHeaders(true);
    // The body must be a JSON array of strings
    return this.http.put<HomeDesign>(this.base_path, imageList, { headers })
      .pipe(
        tap(() => this.refreshData.next()),
        catchError(this.handleError)
      );
  }

  // ---------- Admin POST (same as PUT) ----------
  createHomeDesign(imageList: string[]): Observable<HomeDesign> {
    const headers = this.getHeaders(true);
    return this.http.post<HomeDesign>(this.base_path, imageList, { headers })
      .pipe(
        tap(() => this.refreshData.next()),
        catchError(this.handleError)
      );
  }
}